import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ChatUI, { Bubble, type MessageProps } from '@chatui/core';
import {
  Composer,
  type ComposerHandle,
  type ComposerProps,
} from '@chatui/core/lib/components/Composer';
import { Tooltip } from 'antd';
import type { MediaSendProgressHandler, VoiceNote } from '../runtime/contracts';
import type { ChatMessage } from '../matrix/message';
import { MessageContent } from './MessageContent';
import { useTranslate } from '@/translations';
import { ImageComposer } from './ImageComposer';
import {
  VoiceNoteRecorder,
  type VoiceNoteRecorderHandle,
} from './VoiceNoteRecorder';
import { createLatestEventObserver } from '../matrix/readState';
import type { ComposerActivityKind } from './ComposerActivityTray';
import {
  ConversationImageGallery,
  type ConversationImage,
} from './ConversationImageGallery';

interface ChatConversationProps {
  messages: readonly ChatMessage[];
  matrixUserId: string | null;
  peerDisplayName: string;
  runtimeReady: boolean;
  hasMore: boolean;
  isLoadingOlder: boolean;
  loadOlder(): Promise<void>;
  onSendText(body: string): Promise<void> | void;
  onRetry(eventId: string): void;
  onSendImage(file: File, onProgress: MediaSendProgressHandler): Promise<void>;
  onSendVoice(
    note: VoiceNote,
    onProgress: MediaSendProgressHandler,
  ): Promise<void>;
  onStartCall(): Promise<void> | void;
  onLatestVisible(eventId: string): void;
  onLatestHidden(eventId: string): void;
}

interface LatestMessageProps {
  eventId: string;
  onVisible(eventId: string): void;
  onHidden(eventId: string): void;
  children: React.ReactNode;
}

const LatestMessage = ({
  eventId,
  onVisible,
  onHidden,
  children,
}: LatestMessageProps) => {
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof IntersectionObserver === 'undefined')
      return () => undefined;
    const disconnect = createLatestEventObserver(
      element,
      () => onVisible(eventId),
      () => onHidden(eventId),
    );
    return () => {
      onHidden(eventId);
      disconnect();
    };
  }, [eventId, onHidden, onVisible]);

  return <div ref={elementRef}>{children}</div>;
};

interface ProjectedMessage {
  signature: string;
  value: MessageProps;
}

interface ChatMessagesHandle {
  ref: React.RefObject<HTMLDivElement>;
  scrollToEnd(options?: { animated?: boolean; force?: boolean }): void;
}

const ComposerActivityContext = createContext<React.ReactNode>(null);

const ComposerWithActivity = forwardRef<ComposerHandle, ComposerProps>(
  function ComposerWithActivity(props, ref) {
    const activity = useContext(ComposerActivityContext);
    return (
      <>
        {activity}
        <Composer {...props} ref={ref} />
      </>
    );
  },
);

interface ComposerActionProps {
  disabled: boolean;
  active?: boolean;
  icon: string;
  label: string;
  tooltip: string;
  onClick(): void;
}

const ComposerAction = ({
  disabled,
  active,
  icon,
  label,
  tooltip,
  onClick,
}: ComposerActionProps) => {
  const button = (
    <button
      type="button"
      className={`Btn IconBtn${active ? ' is-active' : ''}`}
      disabled={disabled}
      aria-label={label}
      onClick={onClick}
    >
      <svg className="Icon" aria-hidden="true">
        <use href={icon} />
      </svg>
    </button>
  );
  return (
    <Tooltip
      title={tooltip}
      mouseEnterDelay={0}
      mouseLeaveDelay={0}
      destroyOnHidden
      trigger={['hover', 'focus']}
    >
      {disabled ? (
        <span className="command-centre-chat__tooltip-trigger" tabIndex={0}>
          {button}
        </span>
      ) : (
        button
      )}
    </Tooltip>
  );
};

const messageSignature = (message: ChatMessage, peerDisplayName: string) =>
  JSON.stringify([
    message.kind,
    message.senderId,
    message.timestamp,
    message.sendStatus,
    message.body,
    message.mediaMxc,
    message.thumbnailMxc,
    message.mimeType,
    message.size,
    message.width,
    message.height,
    message.durationMs,
    message.waveform,
    message.isVoice,
    message.call,
    message.receiptEventId,
    peerDisplayName,
  ]);

export const ChatConversation = (props: ChatConversationProps) => {
  const { t } = useTranslate();
  const { hasMore, isLoadingOlder, loadOlder } = props;
  const [activeActivity, setActiveActivity] =
    useState<ComposerActivityKind | null>(null);
  const [selectedImageEventId, setSelectedImageEventId] = useState<
    string | null
  >(null);
  const recorderRef = useRef<VoiceNoteRecorderHandle>(null);
  const projectedMessagesRef = useRef(new Map<string, ProjectedMessage>());
  const messagesRef = useRef<ChatMessagesHandle>(null!);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const anchorHeightRef = useRef<number | null>(null);
  const loadingOlderRef = useRef(false);
  const initiallyScrolledRef = useRef(false);
  const chatUiMessages = useMemo<MessageProps[]>(() => {
    const next = new Map<string, ProjectedMessage>();
    const projected = props.messages.map((message) => {
      const signature = messageSignature(message, props.peerDisplayName);
      const previous = projectedMessagesRef.current.get(message.eventId);
      if (previous?.signature === signature) {
        next.set(message.eventId, previous);
        return previous.value;
      }
      const value: MessageProps = {
        _id: message.eventId,
        type: message.kind,
        content: message,
        createdAt: message.timestamp,
        position:
          message.kind === 'call'
            ? 'center'
            : message.senderId === props.matrixUserId
              ? 'right'
              : 'left',
        user:
          message.kind === 'call' || message.senderId === props.matrixUserId
            ? undefined
            : { name: props.peerDisplayName },
      };
      next.set(message.eventId, { signature, value });
      return value;
    });
    projectedMessagesRef.current = next;
    return projected;
  }, [props.matrixUserId, props.messages, props.peerDisplayName]);
  const conversationImages = useMemo<ConversationImage[]>(
    () =>
      props.messages.flatMap((message) =>
        message.kind === 'image' && message.mediaMxc
          ? [
              {
                eventId: message.eventId,
                mxc: message.mediaMxc,
                alt: message.body,
              },
            ]
          : [],
      ),
    [props.messages],
  );
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const scroller = event.currentTarget;
      scrollerRef.current = scroller;
      if (
        scroller.scrollTop > 48 ||
        !hasMore ||
        isLoadingOlder ||
        loadingOlderRef.current
      )
        return;
      loadingOlderRef.current = true;
      anchorHeightRef.current = scroller.scrollHeight;
      void loadOlder()
        .catch(() => undefined)
        .finally(() => {
          loadingOlderRef.current = false;
        });
    },
    [hasMore, isLoadingOlder, loadOlder],
  );

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const previousHeight = anchorHeightRef.current;
    if (!scroller || previousHeight === null) return;
    scroller.scrollTop += scroller.scrollHeight - previousHeight;
    anchorHeightRef.current = null;
  }, [chatUiMessages]);

  useLayoutEffect(() => {
    if (initiallyScrolledRef.current || chatUiMessages.length === 0) return;
    messagesRef.current?.scrollToEnd({ animated: false, force: true });
    initiallyScrolledRef.current = true;
  }, [chatUiMessages.length]);
  const latestMessage = props.messages
    .slice()
    .reverse()
    .find((message) => message.sendStatus === 'sent');
  const handleImageActivityChange = useCallback((active: boolean) => {
    setActiveActivity((current) =>
      active ? 'images' : current === 'images' ? null : current,
    );
  }, []);
  const handleVoiceActivityChange = useCallback((active: boolean) => {
    setActiveActivity((current) =>
      active ? 'voice' : current === 'voice' ? null : current,
    );
  }, []);
  const imageBlocked = activeActivity === 'voice';
  const voiceBlocked = activeActivity === 'images';
  const voiceBusy = activeActivity === 'voice';

  return (
    <ImageComposer
      disabled={!props.runtimeReady}
      blockedByVoice={imageBlocked}
      onActivityChange={handleImageActivityChange}
      onSend={props.onSendImage}
    >
      {({ openPicker, activity: imageActivity }) => (
        <div className="command-centre-chat">
          <svg className="command-centre-chat__icon-symbols" aria-hidden="true">
            <symbol id="icon-image" viewBox="0 0 24 24">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </symbol>
            <symbol id="icon-mic" viewBox="0 0 24 24">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
            </symbol>
            <symbol id="icon-phone" viewBox="0 0 24 24">
              <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.61 21 3 13.39 3 4c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </symbol>
          </svg>
          <div className="command-centre-chat__toolbar">
            <ComposerAction
              disabled={!props.runtimeReady || imageBlocked}
              active={activeActivity === 'images'}
              icon="#icon-image"
              label={t('commsAttachImage')}
              tooltip={
                imageBlocked
                  ? t('commsFinishVoiceBeforeImages')
                  : t('commsAttachImage')
              }
              onClick={openPicker}
            />
            <ComposerAction
              disabled={!props.runtimeReady || voiceBlocked || voiceBusy}
              active={activeActivity === 'voice'}
              icon="#icon-mic"
              label={t('commsRecordVoiceNote')}
              tooltip={
                voiceBlocked
                  ? t('commsFinishImagesBeforeVoice')
                  : voiceBusy
                    ? t('commsVoiceActivityInProgress')
                    : t('commsRecordVoiceNote')
              }
              onClick={() => void recorderRef.current?.start()}
            />
            <ComposerAction
              disabled={!props.runtimeReady}
              icon="#icon-phone"
              label={t('commsCallStart')}
              tooltip={t('commsCallStart')}
              onClick={() => void props.onStartCall()}
            />
          </div>
          <ComposerActivityContext.Provider
            value={
              <>
                {imageActivity}
                <VoiceNoteRecorder
                  ref={recorderRef}
                  hideTrigger
                  disabled={!props.runtimeReady || voiceBlocked}
                  onActivityChange={handleVoiceActivityChange}
                  onRecorded={props.onSendVoice}
                />
              </>
            }
          >
            <ChatUI
              locale="en-US"
              renderNavbar={() => null}
              messages={chatUiMessages}
              messagesRef={messagesRef}
              onScroll={handleScroll}
              renderBeforeMessageList={() =>
                props.isLoadingOlder ? (
                  <div className="command-centre-chat__history-status">
                    {t('commsLoadingEarlierMessages')}
                  </div>
                ) : !props.hasMore && props.messages.length > 0 ? (
                  <div className="command-centre-chat__history-status">
                    {t('commsBeginningOfConversation')}
                  </div>
                ) : null
              }
              placeholder={t('commsMessagePlaceholder')}
              inputOptions={{ disabled: !props.runtimeReady }}
              onSend={(type, content) => {
                const body = content.trim();
                if (type === 'text' && body && props.runtimeReady)
                  void props.onSendText(body);
              }}
              renderMessageContent={(message) => {
                const content = (
                  <Bubble type={message.type}>
                    <MessageContent
                      message={message.content as ChatMessage}
                      matrixUserId={props.matrixUserId}
                      onRetry={props.onRetry}
                      onOpenImage={setSelectedImageEventId}
                    />
                  </Bubble>
                );
                return message._id === latestMessage?.eventId ? (
                  <LatestMessage
                    eventId={
                      latestMessage.receiptEventId ?? latestMessage.eventId
                    }
                    onVisible={props.onLatestVisible}
                    onHidden={props.onLatestHidden}
                  >
                    {content}
                  </LatestMessage>
                ) : (
                  content
                );
              }}
              Composer={ComposerWithActivity}
            />
          </ComposerActivityContext.Provider>
          {selectedImageEventId && (
            <ConversationImageGallery
              images={conversationImages}
              selectedEventId={selectedImageEventId}
              onClose={() => setSelectedImageEventId(null)}
            />
          )}
        </div>
      )}
    </ImageComposer>
  );
};
